import { Anchor, Box, Button, Card, Center, Grid, Pagination, Select, Stack, Table, Text, Title } from '@mantine/core';
import { IconChartBarOff } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { dateToEndOfDay, dateToStartOfDay, daysAgoISODate, todayISODate } from '../../helpers/dateUtils';
import useFetch from '../../helpers/useFetch';
import LineChart from '../Charts/LineChart';
import ErrorMessage from '../ErrorMessage';
import MetricsSelector from '../MetricsSelector';
import Spinner from '../Spinner';

const formatPercent = (numerator, denominator) => {
  if (!denominator) return null;
  const pct = (numerator / denominator) * 100;
  return `${pct.toFixed(1)}%`;
};

const Metric = ({ title, value, percentOf }) => (
  <Box ta="center">
    <Text size="sm" c="dimmed">
      {title}
    </Text>
    <Text size="xl" fw={600}>
      {(value || 0).toLocaleString(navigator.language)}
    </Text>
    {percentOf ? (
      <Text size="xs" c="dimmed">
        {percentOf}
      </Text>
    ) : null}
  </Box>
);

const SuperAdminOperationalEmails = () => {
  const fetch = useFetch();
  const [kinds, setKinds] = useState();
  const [actions, setActions] = useState([]);
  const [carrier, setCarrier] = useState(null);
  const [kind, setKind] = useState();
  const [hasAction, setHasAction] = useState(null);
  const [page, setPage] = useState(1);
  const [startDate, setStartDate] = useState(daysAgoISODate(29));
  const [endDate, setEndDate] = useState(todayISODate());
  const [stats, setStats] = useState();
  const [loadError, setLoadError] = useState();
  const [retryNonce, setRetryNonce] = useState(0);

  useEffect(() => {
    fetch('/api/admin/operational-emails/kinds')
      .then(({ kinds: ks, actions: acts, carrier: c }) => {
        setKinds(ks);
        setActions(acts || []);
        setCarrier(c || null);
        if (ks?.length && !kind) setKind(ks[0].key);
      })
      .catch(setLoadError);
    // mount-only — kind is read defensively (only set if unset)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter changes reset to page 1 in the same render via wrapper setters
  // (below), so no separate page-reset effect is needed — that pattern caused
  // a duplicate fetch when changing filters from page > 1.
  const changeKind = (v) => {
    setPage(1);
    setKind(v);
  };
  const changeHasAction = (v) => {
    setPage(1);
    setHasAction(v);
  };
  const changeStartDate = (v) => {
    setPage(1);
    setStartDate(v);
  };
  const changeEndDate = (v) => {
    setPage(1);
    setEndDate(v);
  };

  // Single effect with cancellation so a stale fetch can't overwrite a fresh one.
  useEffect(() => {
    if (!kind) return undefined;
    let cancelled = false;
    setStats(undefined);
    setLoadError(undefined);

    const params = new URLSearchParams({
      from: dateToStartOfDay(startDate, 'local'),
      to: dateToEndOfDay(endDate, 'local'),
      kind,
      page: String(page),
    });
    if (hasAction) params.set('hasAction', hasAction);

    fetch(`/api/admin/operational-emails/stats?${params.toString()}`)
      .then((r) => {
        if (!cancelled) setStats(r);
      })
      .catch((e) => {
        if (!cancelled) setLoadError(e);
      });

    return () => {
      cancelled = true;
    };
  }, [kind, startDate, endDate, hasAction, page, retryNonce, fetch]);

  const totalsCount = (key) => stats?.statsByAction?.[key]?.count || 0;
  const sent = totalsCount('sent');
  const opened = totalsCount('opens');

  const chartSeries = actions
    .map((a) => ({
      id: a.label,
      data: stats?.dailyStatsByAction?.[a.key] || [],
    }))
    .filter((s) => s.data.length > 0);

  const buildEmailHref = (emailId) =>
    carrier ? `/accounts/${carrier.accountId}/domains/${carrier.domainId}/emails/${emailId}/analytics` : null;

  const empty = stats && sent === 0 && (stats.emails || []).length === 0;
  const filterEmpty = stats && hasAction && (stats.emails || []).length === 0 && sent > 0;

  return (
    <Card withBorder>
      <Card.Section inheritPadding py="md">
        <Grid align="flex-end" gutter="md">
          <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
            <Select
              label="Kind"
              value={kind || null}
              onChange={changeKind}
              disabled={!kinds}
              data={(kinds || []).map((k) => ({ value: k.key, label: k.displayName }))}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
            <Select
              label="Filter sends"
              placeholder="All sends"
              value={hasAction}
              onChange={changeHasAction}
              clearable
              data={actions.map((a) => ({ value: a.key, label: `Has ${a.label.toLowerCase()}` }))}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 4 }}>
            <MetricsSelector
              startDate={startDate}
              endDate={endDate}
              setStartDate={changeStartDate}
              setEndDate={changeEndDate}
              rowSize="12"
            />
          </Grid.Col>
        </Grid>

        {loadError ? (
          <Box mt="md">
            <ErrorMessage message={loadError.message || 'Failed to load stats'} />
            <Center mt="sm">
              <Button variant="light" onClick={() => setRetryNonce((n) => n + 1)}>
                Retry
              </Button>
            </Center>
          </Box>
        ) : null}

        {kinds && kinds.length === 0 ? (
          <Center py="xl">
            <Text c="dimmed">No operational email kinds configured.</Text>
          </Center>
        ) : !stats && !loadError ? (
          <Spinner />
        ) : empty ? (
          <Center py="xl">
            <Stack align="center" gap="xs">
              <IconChartBarOff size={48} color="var(--mantine-color-dimmed)" stroke={1.5} />
              <Text c="dimmed">No operational emails sent in this period.</Text>
            </Stack>
          </Center>
        ) : stats ? (
          <>
            <Grid mt="md">
              {actions.map((a) => {
                const count = totalsCount(a.key);
                let percentOf = null;
                if (a.key === 'deliveries') percentOf = formatPercent(count, sent);
                else if (a.key === 'opens') percentOf = formatPercent(count, totalsCount('deliveries') || sent);
                else if (a.key === 'clicks') percentOf = formatPercent(count, opened || sent);
                else if (a.key === 'bounces' || a.key === 'complaints') percentOf = formatPercent(count, sent);
                return (
                  <Grid.Col key={a.key} span={{ base: 6, sm: 4, md: 2 }}>
                    <Metric title={a.label} value={count} percentOf={percentOf} />
                  </Grid.Col>
                );
              })}
            </Grid>

            <Box mt="md">
              <LineChart data={chartSeries} title="Daily engagement" />
            </Box>

            <Title order={5} mt="md" mb="xs">
              Sends
            </Title>
            {stats.emails?.length ? (
              <>
                <Table highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Subject</Table.Th>
                      <Table.Th>Created</Table.Th>
                      {actions.map((a) => (
                        <Table.Th key={a.key}>{a.label}</Table.Th>
                      ))}
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {stats.emails.map((e) => {
                      const href = buildEmailHref(e.id);
                      return (
                        <Table.Tr key={e.id}>
                          <Table.Td>
                            {href ? (
                              <Anchor component={Link} to={href}>
                                {e.subject}
                              </Anchor>
                            ) : (
                              <Text>{e.subject}</Text>
                            )}
                            <Text size="xs" c="dimmed">
                              {e.name}
                            </Text>
                          </Table.Td>
                          <Table.Td>{new Date(e.createdAt).toLocaleDateString()}</Table.Td>
                          {actions.map((a) => (
                            <Table.Td key={a.key}>
                              {(e.counts?.[a.key]?.count || 0).toLocaleString(navigator.language)}
                            </Table.Td>
                          ))}
                        </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </Table>
                {stats.pagesCount > 1 ? (
                  <Center mt="md">
                    <Pagination total={stats.pagesCount} value={page} onChange={setPage} />
                  </Center>
                ) : null}
              </>
            ) : (
              <Text c="dimmed">{filterEmpty ? 'No sends matching the current filter.' : 'No sends in this period.'}</Text>
            )}
          </>
        ) : null}
      </Card.Section>
    </Card>
  );
};

export default SuperAdminOperationalEmails;
